import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Loader2 } from "lucide-react";

/**
 * "Albümü indir" — organizatör ve admin galeri sayfalarının ORTAK düğmesi
 * (Admin ⇄ Organizer eşitliği). Her uygulama KENDİ uç tabanını verir:
 * organizatör `/api/organizer/w/:slug`, admin `/api/admin/w/:slug`.
 *
 * İndirme düz bir bağlantı (`<a download>`): oturum çerezi gider, tarayıcı
 * akışı doğrudan diske yazar → GB'lık albümde bile sekme belleği şişmez.
 * Yalnız onaylı kareler girer (sunucu garanti eder). Albüm büyükse sunucu
 * parçalara böler; düğme parça listesine döner.
 */
export interface AlbumExportInfo {
  total: number;
  bytes: number;
  partSize: number;
  parts: number;
}

export function AlbumExportButton({
  apiBase,
  onNotify,
}: {
  apiBase: string;
  /** Uygulamanın kendi toast'u — indirme başladı / bilgi alınamadı. */
  onNotify?: (msg: string, ok?: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const [info, setInfo] = useState<AlbumExportInfo | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setFailed(false);
    fetch(`${apiBase}/media/export-info`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((json: { ok?: boolean; data?: AlbumExportInfo }) => {
        if (!alive) return;
        if (json.ok && json.data) setInfo(json.data);
        else setFailed(true);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [apiBase]);

  const size = (bytes: number) => {
    const gb = bytes / 1024 ** 3;
    const nf = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 });
    return gb >= 1 ? `${nf.format(gb)} GB` : `${nf.format(Math.max(1, Math.round(bytes / 1024 ** 2)))} MB`;
  };

  const started = () => onNotify?.(t("albumExport.started"));

  const btn =
    "inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium transition-colors cursor-pointer hover:bg-primary/5 hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

  if (failed) {
    return <p className="text-xs text-muted-foreground">{t("albumExport.failed")}</p>;
  }
  if (!info) {
    return (
      <span className={`${btn} opacity-60 pointer-events-none`} aria-busy="true">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        {t("albumExport.button")}
      </span>
    );
  }
  if (info.total === 0) {
    return (
      <span className={`${btn} opacity-50 cursor-not-allowed`} title={t("albumExport.empty")} aria-disabled="true">
        <Download className="h-4 w-4" aria-hidden="true" />
        {t("albumExport.button")}
      </span>
    );
  }

  const meta = t("albumExport.meta", { count: info.total, size: size(info.bytes) });

  if (info.parts <= 1) {
    return (
      <a href={`${apiBase}/media/export.zip`} download onClick={started} className={btn} title={t("albumExport.onlyApproved")}>
        <Download className="h-4 w-4" aria-hidden="true" />
        {t("albumExport.button")}
        <span className="text-xs font-normal text-muted-foreground">{meta}</span>
      </a>
    );
  }

  return (
    <details className="relative">
      <summary className={`${btn} list-none [&::-webkit-details-marker]:hidden`}>
        <Download className="h-4 w-4" aria-hidden="true" />
        {t("albumExport.button")}
        <span className="text-xs font-normal text-muted-foreground">{meta}</span>
      </summary>
      <div className="absolute right-0 z-20 mt-2 w-72 rounded-2xl border border-border bg-card p-3 shadow-xl">
        <p className="px-1 pb-2 text-xs text-muted-foreground">{t("albumExport.partsHint", { parts: info.parts })}</p>
        <ul className="space-y-1">
          {Array.from({ length: info.parts }, (_, i) => {
            const from = i * info.partSize + 1;
            const to = Math.min(info.total, (i + 1) * info.partSize);
            return (
              <li key={i}>
                <a
                  href={`${apiBase}/media/export.zip?part=${i + 1}`}
                  download
                  onClick={started}
                  className="flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm hover:bg-primary/5 cursor-pointer"
                >
                  {t("albumExport.part", { n: i + 1, from, to })}
                  <Download className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </details>
  );
}
